/**
 * Performance Monitoring Utilities
 *
 * Provides utilities for measuring and logging performance metrics.
 * Used to track tutorial activation time and other performance-critical operations.
 *
 * @module utils/performanceMonitor
 */
/**
 * Performance mark names
 */
export const PERFORMANCE_MARKS = {
    TUTORIAL_START: 'tutorial-start',
    TUTORIAL_READY: 'tutorial-ready',
    TUTORIAL_FIRST_RENDER: 'tutorial-first-render',
} as const;
/**
 * Performance measure names
 */
export const PERFORMANCE_MEASURES = {
    TUTORIAL_ACTIVATION: 'tutorial-activation-time',
    TUTORIAL_RENDER: 'tutorial-render-time',
} as const;
/**
 * Performance thresholds (in milliseconds)
 */
export const PERFORMANCE_THRESHOLDS = {
    TUTORIAL_ACTIVATION: 100, // Target: under 100ms
    TUTORIAL_RENDER: 50, // Target: under 50ms
} as const;
/**
 * Mark the start of a performance measurement
 *
 * @param markName - Name of the performance mark
 */
export function markPerformanceStart(markName: string): void {
    try {
        if (performance && performance.mark) {
            performance.mark(markName);
        }
    }
    catch (error) {
        // Silently fail if performance API is not available
        console.debug('Performance API not available:', error);
    }
}
/**
 * Mark the end of a performance measurement and calculate duration
 *
 * @param startMark - Name of the start mark
 * @param endMark - Name of the end mark
 * @param measureName - Name of the measure
 * @returns Duration in milliseconds, or null if measurement failed
 */
export function measurePerformance(startMark: string, endMark: string, measureName: string): number | null {
    try {
        if (performance && performance.mark && performance.measure) {
            performance.mark(endMark);
            performance.measure(measureName, startMark, endMark);
            const measure = performance.getEntriesByName(measureName)[0];
            if (measure) {
                return measure.duration;
            }
        }
    }
    catch (error) {
        // Silently fail if performance API is not available
        console.debug('Performance measurement failed:', error);
    }
    return null;
}
/**
 * Log performance measurement with threshold comparison
 *
 * @param measureName - Name of the measure
 * @param duration - Duration in milliseconds
 * @param threshold - Performance threshold in milliseconds
 */
export function logPerformance(measureName: string, duration: number, threshold: number): void {
    const status = duration <= threshold ? '✓' : '⚠️';
    console.log(`${status} ${measureName}: ${duration.toFixed(2)}ms (target: <${threshold}ms)`);
}
/**
 * Clear performance marks and measures
 *
 * @param marks - Array of mark names to clear
 * @param measures - Array of measure names to clear
 */
export function clearPerformanceData(marks: string[], measures: string[]): void {
    try {
        if (performance) {
            marks.forEach(mark => {
                if (performance.clearMarks) {
                    performance.clearMarks(mark);
                }
            });
            measures.forEach(measure => {
                if (performance.clearMeasures) {
                    performance.clearMeasures(measure);
                }
            });
        }
    }
    catch (error) {
        // Silently fail if performance API is not available
        console.debug('Performance data clearing failed:', error);
    }
}
/**
 * Measure tutorial activation time
 *
 * Marks the start of tutorial activation and returns a function
 * to mark the end and log the duration.
 *
 * @returns Function to mark the end of tutorial activation
 */
export function measureTutorialActivation(): () => void {
    markPerformanceStart(PERFORMANCE_MARKS.TUTORIAL_START);
    return () => {
        const duration = measurePerformance(PERFORMANCE_MARKS.TUTORIAL_START, PERFORMANCE_MARKS.TUTORIAL_READY, PERFORMANCE_MEASURES.TUTORIAL_ACTIVATION);
        if (duration !== null) {
            logPerformance(PERFORMANCE_MEASURES.TUTORIAL_ACTIVATION, duration, PERFORMANCE_THRESHOLDS.TUTORIAL_ACTIVATION);
        }
        // Clear performance data after logging
        clearPerformanceData([PERFORMANCE_MARKS.TUTORIAL_START, PERFORMANCE_MARKS.TUTORIAL_READY], [PERFORMANCE_MEASURES.TUTORIAL_ACTIVATION]);
    };
}
/**
 * Measure tutorial render time
 *
 * Marks the start of tutorial render and returns a function
 * to mark the end and log the duration.
 *
 * @returns Function to mark the end of tutorial render
 */
export function measureTutorialRender(): () => void {
    markPerformanceStart(PERFORMANCE_MARKS.TUTORIAL_READY);
    return () => {
        const duration = measurePerformance(PERFORMANCE_MARKS.TUTORIAL_READY, PERFORMANCE_MARKS.TUTORIAL_FIRST_RENDER, PERFORMANCE_MEASURES.TUTORIAL_RENDER);
        if (duration !== null) {
            logPerformance(PERFORMANCE_MEASURES.TUTORIAL_RENDER, duration, PERFORMANCE_THRESHOLDS.TUTORIAL_RENDER);
        }
        // Clear performance data after logging
        clearPerformanceData([PERFORMANCE_MARKS.TUTORIAL_READY, PERFORMANCE_MARKS.TUTORIAL_FIRST_RENDER], [PERFORMANCE_MEASURES.TUTORIAL_RENDER]);
    };
}
